import React, { useState, useEffect, useCallback } from 'react';
import { eventService } from '../services';
import { useDialog } from '../context';
import { Panel, PanelHeader, EmptyBox, MUTED } from './surface';
import { SubPanel, Inner, Row, RowText, RemoveX, Btn, TextInput, SegPills, Help } from './host/kit';

/**
 * PartyQuestionsSection — the host asks the room questions; each guest answers
 * their own and everyone can open the roll-up. Options questions tally; free-text
 * questions collect short answers. Brutalist (base) styling.
 *
 * Props:
 *   eventId
 *   isHost  — host sees the add/remove controls on the live event page.
 *   manage  — setup mode (in the create/edit form): host controls only, no
 *             answering or roll-up (there are no guests yet).
 *   variant — "brutalist" (default, black header card), "surface": the 2026-09
 *             event-page kit (white Panel, band-label header, answers behind a
 *             4px black rule, empty state instead of nothing), or "portal": the
 *             host-event wizard's sub-panel (docs/plans/2026-09-host-event-edit,
 *             Step 4 EXTRAS · PARTY QUESTIONS) — only ever used with `manage`.
 */
export default function PartyQuestionsSection({ eventId, isHost = false, manage = false, variant = 'brutalist', mobile = false }) {
  const { showConfirm, showAlert } = useDialog();
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openRollups, setOpenRollups] = useState({});
  const [drafts, setDrafts] = useState({}); // free-text per question id
  const [adding, setAdding] = useState(false);
  const [newQ, setNewQ] = useState({ prompt: '', type: 'options', options: ['', ''] });
  const [busy, setBusy] = useState(false);

  const showHostControls = isHost || manage;

  const load = useCallback(async () => {
    setLoading(true);
    const res = await eventService.getEventQuestions(eventId);
    if (res.success) {
      setQuestions(res.data || []);
      const d = {};
      (res.data || []).forEach((q) => { if (q.type === 'free') d[q.id] = q.myTextAnswer || ''; });
      setDrafts(d);
    }
    setLoading(false);
  }, [eventId]);

  useEffect(() => { load(); }, [load]);

  const replaceQ = (updated) => setQuestions((prev) => prev.map((q) => (q.id === updated.id ? updated : q)));

  const pickOption = async (q, opt) => {
    const next = q.mySelectedOption === opt ? '' : opt; // toggle off
    const res = await eventService.respondToEventQuestion(q.id, { selectedOption: next });
    if (res.success) replaceQ(res.data);
  };

  const saveText = async (q) => {
    const res = await eventService.respondToEventQuestion(q.id, { textAnswer: drafts[q.id] || '' });
    if (res.success) replaceQ(res.data);
  };

  const toggleRollup = (id) => setOpenRollups((o) => ({ ...o, [id]: !o[id] }));

  const addQuestion = async () => {
    const prompt = (newQ.prompt || '').trim();
    if (!prompt) return;
    const options = newQ.type === 'options' ? newQ.options.map((o) => o.trim()).filter(Boolean) : [];
    if (newQ.type === 'options' && options.length < 2) {
      await showAlert({ title: 'Add options', message: 'An options question needs at least two choices.', type: 'error' });
      return;
    }
    setBusy(true);
    const res = await eventService.createEventQuestion(eventId, { prompt, type: newQ.type, options });
    setBusy(false);
    if (!res.success) { await showAlert({ title: 'Could not add', message: res.error?.message || 'Something went wrong.', type: 'error' }); return; }
    setNewQ({ prompt: '', type: 'options', options: ['', ''] });
    setAdding(false);
    load();
  };

  const removeQuestion = async (q) => {
    const ok = await showConfirm({ title: 'Remove this question?', message: q.prompt, confirmLabel: 'Remove', cancelLabel: 'Keep', variant: 'danger' });
    if (!ok) return;
    const res = await eventService.deleteEventQuestion(q.id);
    if (res.success) setQuestions((prev) => prev.filter((x) => x.id !== q.id));
  };

  const surface = variant === 'surface';

  if (variant === 'portal') {
    const setOption = (i, v) => { const o = [...newQ.options]; o[i] = v; setNewQ({ ...newQ, options: o }); };
    return (
      <SubPanel
        title="EVENT QUESTIONS"
        sub="Ask the room a poll or a free-text prompt."
        gap="12"
        testId="party-questions"
        right={showHostControls ? (
          <Btn size="sm" onClick={() => setAdding((a) => !a)} aria-expanded={adding}>{adding ? 'CLOSE' : '+ ADD QUESTION'}</Btn>
        ) : undefined}
      >
        {loading ? (
          <Help>Loading questions…</Help>
        ) : (
          questions.map((q) => (
            <Row key={q.id} pad="12" testId={`party-question-${q.id}`}>
              <RowText title={q.prompt} meta={q.type === 'options' ? `POLL · ${(q.options || []).length} OPTIONS` : 'FREE TEXT'} />
              {showHostControls && (
                <button
                  type="button"
                  onClick={() => removeQuestion(q)}
                  style={{ flexShrink: 0, border: 'none', background: 'none', padding: 0, fontFamily: 'inherit', fontSize: 10, letterSpacing: '.12em', color: '#6B7280', cursor: 'pointer' }}
                >
                  REMOVE
                </button>
              )}
            </Row>
          ))
        )}

        {showHostControls && adding && (
          <Inner testId="party-question-draft">
            <TextInput
              size="sm"
              placeholder="What do you want to ask everyone?"
              value={newQ.prompt}
              onChange={(e) => setNewQ({ ...newQ, prompt: e.target.value })}
              aria-label="Question"
            />
            <SegPills
              small
              ariaLabel="Question type"
              options={[{ key: 'options', label: 'OPTIONS' }, { key: 'free', label: 'FREE TEXT' }]}
              value={newQ.type}
              onChange={(key) => setNewQ({ ...newQ, type: key })}
            />
            {newQ.type === 'options' && (
              <>
                {newQ.options.map((opt, i) => (
                  <span key={i} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <TextInput size="xs" placeholder={`Option ${i + 1}`} value={opt} onChange={(e) => setOption(i, e.target.value)} aria-label={`Option ${i + 1}`} />
                    {newQ.options.length > 2 && (
                      <RemoveX label={`Remove option ${i + 1}`} onClick={() => setNewQ({ ...newQ, options: newQ.options.filter((_, j) => j !== i) })} />
                    )}
                  </span>
                ))}
                <Btn
                  variant="plain"
                  self="start"
                  style={{ height: 'auto', padding: '4px 0', fontSize: 10 }}
                  onClick={() => setNewQ({ ...newQ, options: [...newQ.options, ''] })}
                >
                  + ADD OPTION
                </Btn>
              </>
            )}
            <Btn full disabled={busy || !newQ.prompt.trim()} onClick={addQuestion}>POST QUESTION</Btn>
          </Inner>
        )}
      </SubPanel>
    );
  }

  if (loading) return surface ? <div className="font-mono" style={{ padding: 16, fontSize: 12, color: MUTED }}>Loading questions…</div> : null;
  if (!showHostControls && questions.length === 0) {
    return surface
      ? <EmptyBox title="NO QUESTIONS YET." mobile={mobile} testId="qa-empty">The host has not asked the room anything for this one.</EmptyBox>
      : null;
  }

  const answeredMine = questions.filter((q) => (q.type === 'options' ? !!q.mySelectedOption : !!q.myTextAnswer)).length;
  const addButton = showHostControls && (
    <button
      type="button"
      className={surface
        ? 'border-2 border-black bg-white px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-wider transition-colors duration-200 hover:bg-black hover:text-white'
        : 'font-mono text-[11px] font-semibold uppercase tracking-wider border-2 border-white/40 px-3 py-1.5 hover:bg-white hover:text-black shrink-0'}
      onClick={() => setAdding((a) => !a)}
    >
      {adding ? 'Close' : '+ Add'}
    </button>
  );

  const Wrapper = surface ? Panel : 'div';
  const wrapperProps = surface ? { 'data-testid': 'party-questions' } : { className: `border-2 border-black ${manage ? '' : 'mb-8'}` };

  return (
    <Wrapper {...wrapperProps}>
      {surface ? (
        <PanelHeader label="EVENT QUESTIONS" right={showHostControls ? addButton : `${answeredMine} OF ${questions.length} ANSWERED`} dense={mobile} />
      ) : (
      <div className="bg-black text-white p-5 flex items-start justify-between gap-4">
        <div>
          <div className="font-mono text-xs font-semibold uppercase tracking-wider">Event Questions</div>
          <p className="font-mono text-xs text-gray-300 mt-2 max-w-md leading-relaxed">
            {manage
              ? 'Ask the room a poll or a free-text prompt. Guests answer on the event page once it’s live; you’ll see the roll-up there.'
              : 'Questions from the host for the crew. Answer your own — everyone can open the roll-up.'}
          </p>
        </div>
        {addButton}
      </div>
      )}

      {showHostControls && adding && (
        <div className="p-4 border-b-2 border-black bg-gray-50">
          <input className="w-full px-3 py-2 border-2 border-black bg-white font-mono text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-black" placeholder="What do you want to ask everyone?" value={newQ.prompt} onChange={(e) => setNewQ({ ...newQ, prompt: e.target.value })} />
          <div className="flex gap-2 mt-3">
            <button className={'font-mono text-[11px] font-semibold uppercase tracking-wider border-2 border-black px-3 py-1 ' + (newQ.type === 'options' ? 'bg-black text-white' : 'bg-white text-black')} onClick={() => setNewQ({ ...newQ, type: 'options' })}>Options</button>
            <button className={'font-mono text-[11px] font-semibold uppercase tracking-wider border-2 border-black px-3 py-1 ' + (newQ.type === 'free' ? 'bg-black text-white' : 'bg-white text-black')} onClick={() => setNewQ({ ...newQ, type: 'free' })}>Free text</button>
          </div>
          {newQ.type === 'options' && (
            <div className="mt-3 space-y-2">
              {newQ.options.map((opt, i) => (
                <div key={i} className="flex gap-2">
                  <input className="flex-1 px-3 py-2 border-2 border-black bg-white font-mono text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-black" placeholder={`Option ${i + 1}`} value={opt} onChange={(e) => { const o = [...newQ.options]; o[i] = e.target.value; setNewQ({ ...newQ, options: o }); }} />
                  {newQ.options.length > 2 && (
                    <button className="font-mono text-xs uppercase text-gray-500 hover:text-black px-2" onClick={() => setNewQ({ ...newQ, options: newQ.options.filter((_, j) => j !== i) })}>×</button>
                  )}
                </div>
              ))}
              <button className="font-mono text-[11px] font-semibold uppercase tracking-wider text-gray-600 hover:text-black" onClick={() => setNewQ({ ...newQ, options: [...newQ.options, ''] })}>+ Add option</button>
            </div>
          )}
          <div className="flex justify-end mt-3">
            <button className="font-mono text-xs font-semibold uppercase tracking-wider border-2 border-black bg-black text-white px-4 py-2 hover:bg-white hover:text-black disabled:opacity-40" disabled={busy || !newQ.prompt.trim()} onClick={addQuestion}>Post question</button>
          </div>
        </div>
      )}

      {questions.length === 0 ? (
        <div className="font-mono text-sm text-gray-500 p-5">No questions yet.</div>
      ) : (
        questions.map((q) => {
          const answered = q.type === 'options' ? !!q.mySelectedOption : !!q.myTextAnswer;
          const open = !!openRollups[q.id];
          return (
            <div key={q.id} className="px-4 py-4 border-b border-gray-200">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-2 min-w-0">
                  <span className={'font-mono text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 shrink-0 ' + (q.type === 'options' ? 'bg-black text-white' : 'border-2 border-black text-black')}>{q.type === 'options' ? 'Poll' : 'Free'}</span>
                  <div className="font-mono font-semibold">{q.prompt}</div>
                </div>
                {showHostControls && <button className="font-mono text-[11px] uppercase tracking-wider text-gray-500 hover:text-black shrink-0" onClick={() => removeQuestion(q)}>Remove</button>}
              </div>

              {manage && q.type === 'options' && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {q.options.map((opt) => (
                    <span key={opt} className="font-mono text-[11px] uppercase tracking-wider border-2 border-gray-300 text-gray-700 px-3 py-1">{opt}</span>
                  ))}
                </div>
              )}
              {manage && q.type === 'free' && (
                <div className="mt-2 font-mono text-[11px] text-gray-500">Guests type their own answer.</div>
              )}

              {!manage && (
              <>
              <div className="mt-3">
                <div className="font-mono text-[10px] uppercase tracking-wider text-gray-500 mb-2">Your answer</div>
                {q.type === 'options' ? (
                  <div className="flex flex-wrap gap-2">
                    {q.options.map((opt) => (
                      <button key={opt} className={'font-mono text-[11px] uppercase tracking-wider border-2 px-3 py-1 ' + (q.mySelectedOption === opt ? 'border-black bg-black text-white' : 'border-gray-300 text-gray-700 hover:border-black')} onClick={() => pickOption(q, opt)}>{opt}</button>
                    ))}
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <input className="flex-1 px-3 py-2 border-2 border-black bg-white font-mono text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-black" placeholder="Type your answer…" value={drafts[q.id] ?? ''} onChange={(e) => setDrafts({ ...drafts, [q.id]: e.target.value })} onKeyDown={(e) => { if (e.key === 'Enter') saveText(q); }} />
                    <button className="font-mono text-[11px] font-semibold uppercase tracking-wider border-2 border-black bg-black text-white px-3 hover:bg-white hover:text-black" onClick={() => saveText(q)}>Save</button>
                  </div>
                )}
              </div>

              <div className="mt-3 pt-3 border-t border-gray-200">
                <button className="flex items-center gap-2 w-full" onClick={() => toggleRollup(q.id)}>
                  <span className="font-mono text-[10px] font-semibold uppercase tracking-wider">Summary</span>
                  <span className="font-mono text-[11px] text-gray-500">{q.answeredCount} of {q.totalGuests} answered</span>
                  <span className="ml-auto font-mono text-xs text-gray-500">{open ? '▴' : '▾'}</span>
                </button>
                {open && (
                  <div className="mt-3" style={surface ? { borderLeft: '4px solid #000', paddingLeft: 12 } : undefined} data-testid="qa-rollup">
                    {q.type === 'options' ? (
                      <div className="font-mono text-sm">
                        {q.options.filter((o) => (q.tally?.[o] || 0) > 0).length === 0
                          ? <span className="text-gray-500">No answers yet</span>
                          : q.options.map((o) => (q.tally?.[o] || 0) > 0 && (
                            <div key={o} className="flex justify-between py-0.5"><span>{o}</span><span className="text-gray-600">{q.tally[o]}</span></div>
                          ))}
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {(q.responses || []).length === 0
                          ? <span className="font-mono text-sm text-gray-500">No answers yet</span>
                          : q.responses.map((r, i) => (
                            <div key={i} className="font-mono text-sm"><span className="text-gray-500 text-[10px] uppercase tracking-wider mr-2">{r.who}</span>{r.text}</div>
                          ))}
                      </div>
                    )}
                    {!answered && q.totalGuests > q.answeredCount && (
                      <div className="font-mono text-[11px] text-gray-400 mt-2">Waiting on {q.totalGuests - q.answeredCount} more</div>
                    )}
                  </div>
                )}
              </div>
              </>
              )}
            </div>
          );
        })
      )}
    </Wrapper>
  );
}
